import React, { useState, useEffect } from 'react';
import {
    Typography, Grid, TextField, Paper
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { formatNumber } from '../util/helpers';
import { formatDate } from '../util/helpers';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
    },
    paper: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        boxShadow: theme.shadows[2],
        borderRadius: theme.shape.borderRadius,
    },
    fieldSet: {
        marginTop: `${theme.spacing(2)} !important`,
        paddingTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        position: 'relative',
    },
    legend: {
        fontSize: '1.1rem',
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.paper,
        padding: '0 5px',
        position: 'absolute',
        top: '-10px',
        left: theme.spacing(1.25),
    },
    totalCost: {
        fontSize: '1.2rem',
        fontWeight: 600,
        marginTop: theme.spacing(2),
    },
    subtitle: {
        marginTop: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    subform: {
        padding: theme.spacing(2),
        marginTop: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
    },
    formControl: {
        width: '100%',
        marginTop: `${theme.spacing(2)} !important`,
        marginBottom: theme.spacing(4),
    },
    descriptionField: {
        marginTop: `${theme.spacing(2)} !important`,
    },
    boldText: {
        fontWeight: 'bold',
    },
    charCounter: {
        fontSize: `0.75rem !important`,
        color: theme.palette.text.secondary,
        textAlign: 'right',
        marginTop: theme.spacing(0.5),
    },
}));

const MAX_DESC_LENGTH = 4096;

const ContractForm = ({ ord, sac, fctOnHasErrors, fctOnChanges }) => {
    const classes = useStyles();

	const offerData = ord.offer;	
	const contractData = ord.contract ? ord.contract : {};		

    const [contractText, setContractText] = useState(contractData?.contractText || '');
    const initialStartDate = contractData?.startDate || new Date().toISOString().slice(0, 10);
    const [startDate, setStartDate] = useState(initialStartDate);
    const initialEndDate = contractData?.endDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const [endDate, setEndDate] = useState(initialEndDate);

    useEffect(() => {
        validateForm();
        if (fctOnChanges) {
            fctOnChanges({
                contractText,
                startDate,
                endDate,
            });
        }
    }, [contractText, startDate, endDate]);

    const validateForm = () => {
        if (fctOnHasErrors) {
            fctOnHasErrors(!contractText || !startDate || !endDate);
        }
    };

    const handleContractTextChange = (event) => {
        const value = event.target.value;
        setContractText(value.slice(0, MAX_DESC_LENGTH)); // Limit to MAX_DESC_LENGTH
    };

    const handleStartDateChange = (event) => {
        setStartDate(event.target.value);
    };

    const handleEndDateChange = (event) => {
        setEndDate(event.target.value);
    };

    return (
        <form className={classes.root}>
            <Paper className={classes.paper}>
                <Typography variant="h6"><strong>Vertragsdetails</strong></Typography>

                <TextField
                    label="Vertragstext"
                    multiline
                    minRows={6}
                    value={contractText}
                    onChange={handleContractTextChange}
                    fullWidth
                    variant="outlined"
                    className={classes.descriptionField}
                    placeholder="Hier den Vertragstext eingeben..."
                />
                <Typography className={classes.charCounter}>
                    {MAX_DESC_LENGTH - contractText.length} Zeichen verbleibend
                </Typography>

                <Grid container spacing={2} className={classes.fieldSet}>
                    {offerData.priceRule === 'flat' ? (
                        <>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Materialkosten (€)"
                                    value={formatNumber(offerData.materialCost, 'EUR', 2)}
                                    fullWidth
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Lohnkosten (€)"
                                    value={formatNumber(offerData.laborCost, 'EUR', 2)}
                                    fullWidth
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Sonstige Kosten (€)"
                                    value={formatNumber(offerData.otherCosts, 'EUR', 2)}
                                    fullWidth
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Stundensatz (€)"
                                    value={formatNumber(offerData.hourlyFee, 'EUR', 2)}
                                    fullWidth
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Anzahl Stunden"
                                    value={offerData.hours}
                                    fullWidth
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>

                <Typography variant="h6" className={classes.totalCost}>
                    Gesamtpreis (brutto): <span className={classes.boldText}>{formatNumber(offerData.totalCost, 'EUR', 2)}</span>
                </Typography>
                <Typography className={classes.subtitle}>
                    inkl. 19% MwSt.
                </Typography>

                <Grid container spacing={2} className={classes.fieldSet}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Vertragsbeginn"
                            type="date"
                            value={formatDate( startDate, 'synapcusDate')}
                            onChange={handleStartDateChange}
                            fullWidth
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Vertragsende"
                            type="date"
                            value={formatDate( endDate, 'synapcusDate')}
                            onChange={handleEndDateChange}
                            fullWidth
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>
                </Grid>
            </Paper>
        </form>
    );
};

export default ContractForm;
